import { useState, useEffect, useCallback } from 'react';
import { FiCpu, FiZap } from 'react-icons/fi';
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid,
} from 'recharts';
import AppLayout from '../components/layout/AppLayout';
import PageHeader from '../components/common/PageHeader';
import Card from '../components/common/Card';
import EmptyState from '../components/common/EmptyState';
import SegmentedControl from '../components/common/SegmentedControl';
import { myAiUsageAPI } from '../services/api';
import { getView, setView, hasView } from '../services/viewCache';
import { useUI } from '../context/UIContext';

// Stale-while-revalidate key (services/viewCache): this route unmounts on nav,
// so the usage charts flashed empty on revisit. Keyed by the day window.
const usageKey = (d) => `myAiUsage:${d}`;

const TEAL = '#145f5f';
const MUTE = '#7a7060';
const BORDER = '#ddd6c6';

const WINDOWS = [
  { key: '7', label: '7 days' },
  { key: '30', label: '30 days' },
  { key: '90', label: '90 days' },
];

const MODE_LABEL = {
  chat: 'Chat', oracle: 'Answers', draft: 'Drafting', agent: 'Actions', vision: 'Documents', other: 'Other',
};

function Stat({ icon: Ico, label, value, sub }) {
  return (
    <Card className="flex flex-col gap-1.5">
      <div className="flex items-center gap-2 text-[10.5px] uppercase tracking-wider font-semibold" style={{ color: MUTE }}>
        <Ico size={13} style={{ color: TEAL }} />
        {label}
      </div>
      <div className="text-[22px] font-bold text-[#08111f]" style={{ fontFamily: 'var(--font-mono, "JetBrains Mono", monospace)' }}>
        {value}
      </div>
      {sub && <div className="text-[11px]" style={{ color: MUTE }}>{sub}</div>}
    </Card>
  );
}

// My AI Usage — the signed-in user's own AI activity: turns, tokens, and
// (when a budget applies) how much of today's allowance is used. Self-scoped;
// admins see platform-wide numbers in the AI Control Plane instead.
export default function MyAiUsagePage() {
  const { showToast } = useUI();
  const [days, setDays] = useState('30');
  // Seed from cache for the default 30-day window so a revisit paints instantly.
  const [data, setData] = useState(() => getView(usageKey('30')) ?? null);
  const [loading, setLoading] = useState(() => !hasView(usageKey('30')));

  const fetchUsage = useCallback(async () => {
    const key = usageKey(days);
    if (!hasView(key)) setLoading(true);
    try {
      const res = await myAiUsageAPI.get(parseInt(days, 10));
      if (res.success) { setData(res.data); setView(key, res.data); }
    } catch (err) {
      // Keep the cached window on a failed refresh; toast only on a first load.
      console.error('Error fetching AI usage:', err);
      if (!hasView(key)) showToast('Failed to load your AI usage', 'error');
    } finally {
      setLoading(false);
    }
  }, [days, showToast]);

  useEffect(() => { fetchUsage(); }, [fetchUsage]);

  const summary = data?.summary || {};
  const budget = data?.budget || {};
  const daily = (data?.dailyUsage || []).map((d) => ({
    date: (d._id || '').slice(5),
    tokens: d.tokens || 0,
  }));
  const byMode = (data?.byMode || []).filter((m) => m.totalCalls > 0);
  const hasBudget = budget.dailyTokenCap != null && budget.dailyTokenCap > 0;
  const budgetPct = hasBudget ? Math.min(100, Math.round(((budget.usedToday || 0) / budget.dailyTokenCap) * 100)) : 0;
  const hasAny = (summary.totalCalls || 0) > 0;

  return (
    <AppLayout>
      <PageHeader
        title="My AI Usage"
        subtitle="Your own AI activity — conversations, drafting, and document reading. Only you and platform admins can see this."
        actions={<SegmentedControl options={WINDOWS} value={days} onChange={setDays} />}
      />

      {/* Today's budget — shown only when a cap actually applies */}
      {hasBudget && (
        <Card className="mb-4">
          <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
            <div className="text-[12px] font-bold text-[#08111f]">Today's AI allowance</div>
            <div className="text-[11.5px]" style={{ color: MUTE }}>
              {(budget.usedToday || 0).toLocaleString('en-IN')} / {budget.dailyTokenCap.toLocaleString('en-IN')} tokens
            </div>
          </div>
          <div className="h-[8px] rounded-full overflow-hidden" style={{ background: '#f0ece2' }}>
            <div
              className="h-full rounded-full transition-all"
              style={{
                width: `${budgetPct}%`,
                background: budgetPct >= 90 ? '#9a564a' : budgetPct >= 70 ? '#b8973a' : TEAL,
              }}
            />
          </div>
          {budgetPct >= 90 && (
            <div className="text-[11px] mt-1.5" style={{ color: '#9a564a' }}>
              You're close to today's limit — it resets at midnight.
            </div>
          )}
        </Card>
      )}

      {!loading && !hasAny ? (
        <EmptyState
          icon={FiCpu}
          tone={TEAL}
          title="No AI activity yet"
          subtitle="Ask the AI Agent about your compliance, or draft a resolution — your usage will show up here."
        />
      ) : (
        <>
          {/* Requests and tokens only. Cost is the platform's, not the user's,
              and response time is an operations number — both belong on the
              admin Usage & Cost screen, not here (Ritik, 22 Sep 2026). */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
            <Stat icon={FiZap} label="AI requests" value={(summary.totalCalls || 0).toLocaleString('en-IN')} sub={`last ${days} days`} />
            <Stat icon={FiCpu} label="Tokens used" value={(summary.totalTokens || 0).toLocaleString('en-IN')} sub={`last ${days} days`} />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Card>
              <div className="text-[12px] font-bold mb-3 text-[#08111f]">Tokens over time</div>
              {daily.length > 1 ? (
                <ResponsiveContainer width="100%" height={190}>
                  <AreaChart data={daily} margin={{ top: 6, right: 10, left: -8, bottom: 0 }}>
                    <defs>
                      <linearGradient id="myTokFill" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor={TEAL} stopOpacity={0.35} />
                        <stop offset="100%" stopColor={TEAL} stopOpacity={0.02} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e8e2d6" vertical={false} />
                    <XAxis dataKey="date" tick={{ fontSize: 10, fill: MUTE }} tickLine={false} axisLine={{ stroke: BORDER }} minTickGap={24} />
                    <YAxis tick={{ fontSize: 10, fill: MUTE }} tickLine={false} axisLine={false} width={52} />
                    <Tooltip formatter={(v) => [v.toLocaleString('en-IN'), 'Tokens']} contentStyle={{ fontSize: 12, borderRadius: 8, border: `1px solid ${BORDER}` }} />
                    <Area type="monotone" dataKey="tokens" stroke={TEAL} strokeWidth={2} fill="url(#myTokFill)" />
                  </AreaChart>
                </ResponsiveContainer>
              ) : (
                <div className="text-[11.5px] py-10 text-center" style={{ color: MUTE }}>Not enough days of activity to chart yet.</div>
              )}
            </Card>

            <Card>
              <div className="text-[12px] font-bold mb-3 text-[#08111f]">What you used AI for</div>
              {byMode.length ? (
                <div className="flex flex-col gap-2.5">
                  {byMode.map((m) => {
                    const maxTokens = Math.max(...byMode.map((x) => x.totalTokens || 0), 1);
                    const pct = Math.max(4, Math.round(((m.totalTokens || 0) / maxTokens) * 100));
                    return (
                      <div key={m._id || 'other'}>
                        <div className="flex items-center justify-between text-[11.5px] mb-1">
                          <span className="font-semibold text-[#08111f]">{MODE_LABEL[m._id] || m._id || 'Other'}</span>
                          <span style={{ color: MUTE }}>{(m.totalCalls || 0).toLocaleString('en-IN')} requests</span>
                        </div>
                        <div className="h-[6px] rounded-full overflow-hidden" style={{ background: '#f0ece2' }}>
                          <div className="h-full rounded-full" style={{ width: `${pct}%`, background: TEAL }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-[11.5px] py-10 text-center" style={{ color: MUTE }}>No activity in this window.</div>
              )}
            </Card>
          </div>
        </>
      )}
    </AppLayout>
  );
}
